import { ContainerRef, APIOutput } from "@amzn/ask-legal-domain";
import {
    Box,
    Button,
    Flashbar,
    FlashbarProps,
    Modal,
    SpaceBetween,
    StatusIndicator,
    Table,
    TableProps
} from "@amzn/awsui-components-react";
import * as React from "react";
import { AppContext } from "../../../setup/context";
import { NewEmployeePreview } from "../../../api/legal-contact.api";

interface SyncContactsModalProps {
    containerRef: ContainerRef;
    instanceId: string;
    onDismiss: () => void;
    onSuccess: () => void;
}

export const SyncContactsModal = (props: SyncContactsModalProps) => {
    const context = React.useContext(AppContext);
    const [loading, setLoading] = React.useState(false);
    const [syncing, setSyncing] = React.useState(false);
    const [newEmployees, setNewEmployees] = React.useState<NewEmployeePreview[]>([]);
    const [selectedItems, setSelectedItems] = React.useState<NewEmployeePreview[]>([]);
    const [flashbarItems, setFlashbarItems] = React.useState<FlashbarProps.MessageDefinition[]>([]);

    const columnDefinitions: TableProps.ColumnDefinition<NewEmployeePreview>[] = [
        {
            id: "name",
            header: "Name",
            cell: (item) => item.name,
            sortingField: "name"
        },
        {
            id: "login",
            header: "Login",
            cell: (item) => item.login,
            sortingField: "login"
        },
        {
            id: "email",
            header: "Email",
            cell: (item) => item.email
        },
        {
            id: "manager",
            header: "Manager",
            cell: (item) => `${item.managerName} (${item.managerLogin})`
        }
    ];

    const loadPreview = React.useCallback(async () => {
        setLoading(true);
        setFlashbarItems([]);
        try {
            const response = await context.getLegalContactAPI().wml.previewSync({
                containerId: props.containerRef.id,
                instanceId: props.instanceId
            });

            const output = APIOutput.fromRaw<any>(response.data);
            if (output.isErr()) {
                throw new Error(output.err.message);
            }

            const result = output.data;
            setNewEmployees(result.newEmployees || []);
            if ((result.count || 0) === 0) {
                setFlashbarItems([{
                    type: "info",
                    content: "No new contacts to add. All eligible employees are already listed.",
                    dismissible: true
                }]);
            }
        } catch (error) {
            const err = error as Error;
            setFlashbarItems([{
                type: "error",
                header: "Failed to load preview",
                content: err.message,
                dismissible: true
            }]);
        } finally {
            setLoading(false);
        }
    }, [props.containerRef.id, props.instanceId, context]);

    const syncSelected = async () => {
        if (selectedItems.length === 0) return;

        setSyncing(true);
        setFlashbarItems([]);

        try {
            const response = await context.getLegalContactAPI().wml.syncContainer({
                containerId: props.containerRef.id,
                employeeManagerPairs: selectedItems.map(emp => ({
                    employeeLogin: emp.login,
                    managerLogin: emp.managerLogin
                }))
            });

            const output = APIOutput.fromRaw<any>(response.data);
            if (output.isErr()) {
                throw new Error(output.err.message);
            }

            const result = output.data;
            const contactsAdded = result.contactsAdded || 0;
            const contactsFailed = result.contactsFailed || 0;
            const failures = result.failures || [];

            if (contactsFailed === 0) {
                // All succeeded
                setFlashbarItems([{
                    type: "success",
                    header: `Successfully added ${contactsAdded} contact${contactsAdded !== 1 ? "s" : ""}`,
                    dismissible: true
                }]);
                setTimeout(() => {
                    props.onSuccess();
                    props.onDismiss();
                }, 1500);
            } else if (contactsAdded === 0) {
                // All failed - show as error (red)
                const failureDetails = failures.map(f => `${f.login}: ${f.error}`).join(", ");
                setFlashbarItems([{
                    type: "error",
                    header: `Failed to add ${contactsFailed} contact${contactsFailed !== 1 ? "s" : ""}`,
                    content: failureDetails || "All contacts failed to add.",
                    dismissible: true
                }]);
            } else {
                // Partial success - show as warning (yellow)
                const failureDetails = failures.map(f => `${f.login}: ${f.error}`).join(", ");
                setFlashbarItems([{
                    type: "warning",
                    header: `Added ${contactsAdded} of ${selectedItems.length} contact${selectedItems.length !== 1 ? "s" : ""}`,
                    content: `${contactsFailed} contact${contactsFailed !== 1 ? "s" : ""} failed to add. ${failureDetails}`,
                    dismissible: true
                }]);
            }
        } catch (error) {
            const err = error as Error;
            const errorMessage = err.message || "An unexpected error occurred";
            setFlashbarItems([{
                type: "error",
                header: "Failed to sync contacts",
                content: errorMessage,
                dismissible: true
            }]);
        } finally {
            setSyncing(false);
        }
    };

    React.useEffect(() => {
        loadPreview();
        setSelectedItems([]);
    }, [loadPreview]);

    return (
        <Modal
            visible={true}
            onDismiss={syncing ? undefined : props.onDismiss}
            header="Sync Contacts"
            size="large"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={props.onDismiss} disabled={syncing}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={syncSelected}
                            disabled={selectedItems.length === 0 || syncing}
                            loading={syncing}
                        >
                            Add Selected ({selectedItems.length})
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="m">
                {flashbarItems.length > 0 && (
                    <Flashbar items={flashbarItems} />
                )}

                {loading ? (
                    <Box textAlign="center" padding="xxl">
                        <StatusIndicator type="loading">
                            Checking for new contacts...
                        </StatusIndicator>
                    </Box>
                ) : newEmployees.length === 0 ? (
                    <Box textAlign="center" padding="xxl" color="text-body-secondary">
                        No new contacts to add.
                    </Box>
                ) : (
                    <>
                        <Box variant="p">
                            <SpaceBetween size="xs">
                                <Box>
                                    <strong>{newEmployees.length}</strong> new employee{newEmployees.length !== 1 ? "s" : ""} found
                                </Box>
                                <Box color="text-body-secondary" fontSize="body-s">
                                    Selected employees will be added as "New Contact" with settings inherited from their manager.
                                    Each employee will receive an email notification to review and update their contact information.
                                </Box>
                            </SpaceBetween>
                        </Box>
                        <Table
                            columnDefinitions={columnDefinitions}
                            items={newEmployees}
                            loading={loading || syncing}
                            loadingText={syncing ? "Adding contacts..." : "Loading contacts"}
                            selectionType="multi"
                            selectedItems={selectedItems}
                            onSelectionChange={({ detail }) =>
                                setSelectedItems(detail.selectedItems)
                            }
                            empty={
                                <Box textAlign="center" color="inherit">
                                    No employees found
                                </Box>
                            }
                            header={
                                <SpaceBetween size="s" direction="horizontal">
                                    <Button
                                        variant="normal"
                                        iconName="check"
                                        onClick={() => setSelectedItems(newEmployees)}
                                        disabled={syncing || selectedItems.length === newEmployees.length}
                                    >
                                        Select all
                                    </Button>
                                    <Button
                                        variant="normal"
                                        iconName="close"
                                        onClick={() => setSelectedItems([])}
                                        disabled={syncing || selectedItems.length === 0}
                                    >
                                        Clear selection
                                    </Button>
                                </SpaceBetween>
                            }
                        />
                    </>
                )}
            </SpaceBetween>
        </Modal>
    );
};
